import {
  isBrowserStorageAvailable as isBrowser,
  isStorageBlocked,
} from "~/lib/utils/safe-storage";
import { reportClientError } from "~/utils/report-client-error";

import {
  GUEST_SAVE_FOR_LATER_KEY,
  type GuestSaveForLater,
  GuestSaveForLaterSchema,
} from "../schemas/save-for-later";

// Fresh object per call — a shared `{ items: [] }` singleton would let one
// caller's mutation leak into every later "empty" read.
const empty = (): GuestSaveForLater => ({ items: [] });

/**
 * Persistence primitives for the guest Save-for-Later list. Same-tab
 * reactivity is owned by `useGuestSaveForLater`; this file is the SSR-safe
 * I/O layer only.
 *
 * COM-63 (P0-D-SFL) — every localStorage touch lives INSIDE a try, including
 * the availability probe: with storage blocked (Chrome "block all cookies")
 * `typeof localStorage` invokes the getter and throws `SecurityError` (typeof
 * only suppresses ReferenceError). This util is reached from the
 * layout-mounted `SaveForLaterProvider`, so an unguarded probe took EVERY
 * page to the root error boundary. `isBrowserStorageAvailable` is the
 * try-wrapped probe from `safe-storage.ts`.
 */
export const guestSaveForLaterUtils = {
  getList: (): GuestSaveForLater => {
    if (!isBrowser()) return empty();
    try {
      const raw = localStorage.getItem(GUEST_SAVE_FOR_LATER_KEY);
      if (!raw) return empty();
      const parsed = GuestSaveForLaterSchema.safeParse(JSON.parse(raw));
      if (parsed.success) return parsed.data;
      console.warn(
        "[guest-save-for-later] localStorage corrupt — resetting",
        parsed.error.message,
      );
      // COM-63 — log-before-degrade: a corrupt list is reset silently for the
      // shopper, but the signal must still reach Workers Logs.
      reportClientError("guest-save-for-later.corrupt", parsed.error);
      localStorage.removeItem(GUEST_SAVE_FOR_LATER_KEY);
    } catch (error) {
      console.error("Error reading guest save-for-later list:", error);
    }
    return empty();
  },

  /**
   * Returns `true` when the write persisted, `false` when it failed (quota,
   * private mode, storage-blocked, SSR) — callers must not report success on
   * a `false` (COM-63 P2-9b; mirrors `guestFavoritesUtils`' null contract).
   * Beacons `guest-save-for-later.write` before returning `false` so the
   * graceful degrade never loses the signal.
   */
  write: (value: GuestSaveForLater): boolean => {
    if (!isBrowser()) {
      // Blocked storage (P0-D-SFL) is a failed write the shopper must hear
      // about, not an SSR no-op — beacon it like a thrown setItem.
      if (isStorageBlocked()) {
        reportClientError(
          "guest-save-for-later.write",
          new Error("localStorage unavailable"),
          { reason: "storage-blocked" },
        );
      }
      return false;
    }
    try {
      localStorage.setItem(GUEST_SAVE_FOR_LATER_KEY, JSON.stringify(value));
      return true;
    } catch (error) {
      console.error("Error writing guest save-for-later list:", error);
      reportClientError("guest-save-for-later.write", error);
      return false;
    }
  },

  clear: (): void => {
    if (!isBrowser()) return;
    try {
      localStorage.removeItem(GUEST_SAVE_FOR_LATER_KEY);
    } catch (error) {
      console.error("Error clearing guest save-for-later list:", error);
      reportClientError("guest-save-for-later.clear", error);
    }
  },
};
